import { useCallback } from 'react';
import { ApiError } from '@alltrails/core/utils/api';
import { post } from '@alltrails/core/utils/post';
import logError from '@alltrails/core/utils/logError';
import logSignUpFailed from '@alltrails/amplitude/events/logSignUpFailed';
import logLogInFailed from '@alltrails/amplitude/events/logLogInFailed';
import { jwtDecode } from 'jwt-decode';
import LoginResponse from '../types/LoginResponse';
import { authSuccessCallback } from '../utils/AuthHandlers';
import useLoadAppleSignIn from './useLoadAppleSignIn';

const appleConstants = {
  CLIENT_ID: 'com.alltrails.AllTrails.web',
  SCOPES: 'name email'
};

const logAppleAuthFailure = (handlerType: 'login' | 'signup', errorMessage: string) => {
  logError(new Error(errorMessage));
  if (handlerType === 'signup') {
    logSignUpFailed({ authentication_type: 'apple', failure_type: errorMessage });
  } else {
    logLogInFailed({ authentication_type: 'apple', failure_type: errorMessage });
  }
};

export default function useApplePopupHandler(isOpen: boolean) {
  const isAppleSignInLoaded = useLoadAppleSignIn(isOpen);

  const applePopupHandler = useCallback(
    async ({
      clientId,
      referralLink,
      handlerType = 'signup',
      grantType
    }: {
      clientId?: string;
      referralLink?: string;
      handlerType?: 'login' | 'signup';
      grantType?: string | null;
    } = {}) => {
      let loginResponse;

      if (!isAppleSignInLoaded || !window.AppleID) {
        logError(new Error('Apple Sign-In SDK not loaded yet'));
        return;
      }

      const redirectURI = `${window.location.protocol}//${window.location.hostname}${
        window.location.port ? `:${window.location.port}` : ''
      }/users/auth/apple/callback`;

      window.AppleID.auth.init({
        clientId: appleConstants.CLIENT_ID,
        scope: appleConstants.SCOPES,
        usePopup: true,
        redirectURI
      });

      // Trigger Apple sign in popup
      const appleResponse: AppleSignInAPI.SignInResponseI & { error?: string } = await window.AppleID.auth.signIn();

      // appleResponse.error is called when the user cancels the sign-in process
      // https://developer.apple.com/documentation/signinwithapple/configuring-your-webpage-for-sign-in-with-apple
      if (appleResponse.error) {
        // Note: It's probably not necessary to log as an error, but will be helpful for debugging for now.
        logAppleAuthFailure(handlerType, `Apple sign-in was cancelled. Error code: ${appleResponse.error}`);
        return;
      }

      // The JWT's "sub" field contains the user identifier.
      // https://developer.apple.com/documentation/signinwithapple/authenticating-users-with-sign-in-with-apple#Retrieve-the-users-information-from-Apple-ID-servers
      const userIdentifier = jwtDecode(appleResponse.authorization.id_token)?.sub;

      if (!userIdentifier) {
        const errorMessage = 'Apple sign-in was unsuccessful. No user identifier found';
        logAppleAuthFailure(handlerType, errorMessage);
        throw new Error(errorMessage);
      }

      try {
        loginResponse = await post<LoginResponse>('/identity/auth/apple', {
          user_identifier: userIdentifier,
          identity_token: appleResponse.authorization.id_token,
          client_id: clientId || process.env.NEXT_PUBLIC_IDENTITY_CLIENT_ID,
          ...(referralLink && { referral_code: referralLink }),
          ...(grantType && { grant_type: grantType }),

          // Apple only returns the user object the first time the user authorizes the app
          // https://developer.apple.com/documentation/signinwithapple/configuring-your-webpage-for-sign-in-with-apple
          ...(appleResponse.user && {
            email: appleResponse.user.email,
            given_name: appleResponse.user.name.firstName,
            family_name: appleResponse.user.name.lastName
          })
        });

        authSuccessCallback(loginResponse, 'apple', loginResponse.is_collab_list_invite);

        return loginResponse;
      } catch (e: any) {
        const failureType = e instanceof ApiError && e.data?.errors?.length > 0 ? e.data.errors[0].code : e.status;
        logAppleAuthFailure(handlerType, failureType);
        throw e;
      }
    },
    [isAppleSignInLoaded]
  );

  return applePopupHandler;
}
